import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, Zap } from "lucide-react";
import { apiAuth } from "@/lib/api";
import { StatusBadge, PIPELINE_STARTUPS } from "@/components/admin/StatusBadge";

const PAGE_SIZES = [25, 50, 100];

const STAGE_OPTIONS = [
  "idea", "pre_seed", "seed", "late_seed", "series_a", "series_b_plus",
];
const SECTOR_OPTIONS = [
  "AI", "Generative AI", "Agentic AI", "SaaS", "B2B Software", "Enterprise Software",
  "FinTech", "InsurTech", "HealthTech", "MedTech", "ClimateTech", "GreenTech",
  "EdTech", "Cybersecurity", "Robotics", "Mobility", "IndustrialTech", "DeepTech",
  "HRTech", "FemTech", "Creator Economy", "Consumer", "Marketplace", "E-Commerce",
  "PropTech", "LegalTech", "Gaming", "Media", "Other",
];
const COUNTRY_OPTIONS = [
  "Germany", "Austria", "Switzerland", "France", "Netherlands", "United Kingdom",
  "United States", "Other",
];

// Quick-Filter Definitionen. Jede liefert ein flaches Params-Patch.
const QUICK_FILTERS = [
  {
    key: "needs_action",
    label: "Needs Action",
    paramsPatch: { status: "submitted,in_review", min_score: "" },
    icon: Zap,
  },
  {
    key: "recent",
    label: "Recently Submitted",
    paramsPatch: () => ({
      submitted_from: new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString().slice(0, 10),
    }),
  },
  {
    key: "high_score",
    label: "High Score ≥ 75",
    paramsPatch: { min_score: "75" },
  },
  {
    key: "has_ai",
    label: "Has AI Analysis",
    paramsPatch: { has_ai: "true" },
  },
];

const MULTI_KEYS = new Set(["status", "stage", "sector"]);

export default function AdminStartupsList() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") || 1);
  const limit = Number(params.get("limit") || 25);
  const q = params.get("q") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [draftQ, setDraftQ] = useState(q);

  // build query string from params
  const query = useMemo(() => {
    const s = new URLSearchParams();
    s.set("page", String(page));
    s.set("limit", String(limit));
    for (const [k, v] of params.entries()) {
      if (["page", "limit"].includes(k)) continue;
      if (v) s.set(k, v);
    }
    return s.toString();
  }, [params, page, limit]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiAuth.get(`/admin/startups?${query}`).then(({ data }) => {
      if (!cancelled) { setData(data); setLoading(false); }
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query]);

  const updateParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === "" || v == null) next.delete(k); else next.set(k, String(v));
    next.set("page", "1");
    setParams(next);
  };

  const toggleMulti = (k, value) => {
    const current = (params.get(k) || "").split(",").filter(Boolean);
    const has = current.includes(value);
    const nextSet = has ? current.filter((v) => v !== value) : [...current, value];
    updateParam(k, nextSet.join(","));
  };

  const isMultiSelected = (k, value) =>
    (params.get(k) || "").split(",").filter(Boolean).includes(value);

  const applyQuickFilter = (qf) => {
    const next = new URLSearchParams(params);
    const patch = typeof qf.paramsPatch === "function" ? qf.paramsPatch() : qf.paramsPatch;
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v == null) next.delete(k); else next.set(k, String(v));
    });
    next.set("page", "1");
    setParams(next);
  };

  const clearAll = () => setParams({});

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const activeFiltersCount = Array.from(params.keys()).filter(
    (k) => !["page", "limit"].includes(k) && params.get(k)
  ).length;

  // Resolve quick-filter "active" state by whether all its keys/values are
  // currently applied. Date-based quick filters cannot really be "active" in
  // a stable way, so we always reset them to off.
  const isQuickActive = (qf) => {
    if (typeof qf.paramsPatch === "function") return false;
    return Object.entries(qf.paramsPatch).every(([k, v]) => {
      const cur = params.get(k);
      if (v === "" || v == null) return !cur;
      return cur === String(v);
    });
  };

  return (
    <div data-testid="page-admin-startups" className="flex flex-col gap-5">
      {/* Quick filter pill row */}
      <div className="flex flex-wrap gap-2" data-testid="quick-filters">
        {QUICK_FILTERS.map((qf) => {
          const Icon = qf.icon;
          const active = isQuickActive(qf);
          return (
            <button
              key={qf.key}
              type="button"
              onClick={() => applyQuickFilter(qf)}
              className={`wit-pill text-xs transition-colors ${
                active
                  ? "bg-raspberry text-white border-raspberry"
                  : "bg-white border-sandBeige text-cocoa hover:border-raspberry"
              }`}
              data-testid={`quick-filter-${qf.key}`}
            >
              {Icon && <Icon size={12} className="inline mr-1.5" />}
              {qf.label}
            </button>
          );
        })}
        {activeFiltersCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-warmGrey underline hover:text-raspberry ml-2"
            data-testid="filters-clear"
          >
            Alle Filter zurücksetzen
          </button>
        )}
      </div>

      {/* Search + filter toggle */}
      <div className="bg-white border border-sandBeige rounded-3xl p-4 flex flex-wrap items-center gap-3">
        <form
          onSubmit={(e) => { e.preventDefault(); updateParam("q", draftQ.trim() || ""); }}
          className="flex items-center gap-2 flex-1 min-w-[200px]"
        >
          <div className="flex items-center gap-2 bg-porcelain rounded-2xl border border-sandBeige px-3 py-2 flex-1">
            <Search size={14} className="text-warmGrey" />
            <input
              className="bg-transparent outline-none text-sm flex-1"
              placeholder="Suche Company / Founder …"
              value={draftQ}
              onChange={(e) => setDraftQ(e.target.value)}
              data-testid="startups-search"
            />
          </div>
        </form>
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className="wit-pill bg-white border-sandBeige hover:border-raspberry text-cocoa"
          data-testid="startups-toggle-filters"
        >
          <SlidersHorizontal size={14} className="inline mr-1.5" />
          Filter {activeFiltersCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-raspberry text-white text-[10px]">
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters — inline on desktop, bottom-sheet on mobile */}
      {showFilters && (
        <>
          {/* mobile bottom-sheet backdrop */}
          <div
            className="md:hidden fixed inset-0 bg-cocoa/40 z-40"
            onClick={() => setShowFilters(false)}
          />
          <div
            className="md:relative md:bg-white md:border md:border-sandBeige md:rounded-3xl md:p-6 md:max-h-none
                       fixed inset-x-0 bottom-0 z-50 bg-white border-t border-sandBeige rounded-t-3xl p-5 max-h-[80vh] overflow-y-auto shadow-soft"
            data-testid="startups-filters"
          >
            <div className="flex items-center justify-between mb-4 md:hidden">
              <p className="font-serif text-lg text-cocoa">Filter</p>
              <button type="button" onClick={() => setShowFilters(false)} aria-label="Schließen">
                <X size={18} />
              </button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <ChipGroup
                label="Status"
                options={PIPELINE_STARTUPS}
                isSelected={(v) => isMultiSelected("status", v)}
                onToggle={(v) => toggleMulti("status", v)}
                testIdPrefix="filter-status"
              />
              <ChipGroup
                label="Stage"
                options={STAGE_OPTIONS}
                isSelected={(v) => isMultiSelected("stage", v)}
                onToggle={(v) => toggleMulti("stage", v)}
                testIdPrefix="filter-stage"
              />
              <ChipGroup
                label="Sector"
                options={SECTOR_OPTIONS}
                isSelected={(v) => isMultiSelected("sector", v)}
                onToggle={(v) => toggleMulti("sector", v)}
                testIdPrefix="filter-sector"
              />
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-cocoa">Country</span>
                <select
                  className="wit-input"
                  value={params.get("country") || ""}
                  onChange={(e) => updateParam("country", e.target.value)}
                  data-testid="filter-country"
                >
                  <option value="">Alle</option>
                  {COUNTRY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-cocoa">AI Score ≥</span>
                <input
                  className="wit-input"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="0–100"
                  value={params.get("min_score") || ""}
                  onChange={(e) => updateParam("min_score", e.target.value)}
                  data-testid="filter-min-score"
                />
              </label>
              <label className="flex items-start justify-between gap-3 bg-porcelain rounded-2xl px-4 py-3 border border-sandBeige cursor-pointer">
                <div className="flex flex-col">
                  <span className="text-sm text-cocoa font-medium">Female-led</span>
                  <span className="text-xs text-warmGrey">Founderin im Team</span>
                </div>
                <input
                  type="checkbox"
                  className="accent-raspberry mt-1"
                  checked={params.get("female_led") === "true"}
                  onChange={(e) => updateParam("female_led", e.target.checked ? "true" : "")}
                  data-testid="filter-female-led"
                />
              </label>
              <label className="flex items-start justify-between gap-3 bg-porcelain rounded-2xl px-4 py-3 border border-sandBeige cursor-pointer">
                <div className="flex flex-col">
                  <span className="text-sm text-cocoa font-medium">Has AI Analysis</span>
                  <span className="text-xs text-warmGrey">mindestens 1 Run</span>
                </div>
                <input
                  type="checkbox"
                  className="accent-raspberry mt-1"
                  checked={params.get("has_ai") === "true"}
                  onChange={(e) => updateParam("has_ai", e.target.checked ? "true" : "")}
                  data-testid="filter-has-ai"
                />
              </label>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-cocoa">Submitted</span>
                <div className="flex gap-2 items-center">
                  <input
                    className="wit-input flex-1"
                    type="date"
                    value={params.get("submitted_from") || ""}
                    onChange={(e) => updateParam("submitted_from", e.target.value)}
                    data-testid="filter-submitted-from"
                  />
                  <span className="text-warmGrey text-xs">–</span>
                  <input
                    className="wit-input flex-1"
                    type="date"
                    value={params.get("submitted_to") || ""}
                    onChange={(e) => updateParam("submitted_to", e.target.value)}
                    data-testid="filter-submitted-to"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5 md:mt-2">
              <button type="button" onClick={clearAll} className="wit-btn-secondary text-xs">
                Zurücksetzen
              </button>
              <button type="button" onClick={() => setShowFilters(false)} className="wit-btn-primary text-xs">
                Anwenden ({total})
              </button>
            </div>
          </div>
        </>
      )}

      {/* Table / Cards */}
      <div className="bg-white border border-sandBeige rounded-3xl overflow-hidden">
        <table className="w-full hidden md:table">
          <thead className="bg-porcelain text-warmGrey">
            <tr>
              <th className="text-left text-xs font-medium px-5 py-3">Company</th>
              <th className="text-left text-xs font-medium px-4 py-3">Stage</th>
              <th className="text-left text-xs font-medium px-4 py-3">Country</th>
              <th className="text-left text-xs font-medium px-4 py-3">AI Score</th>
              <th className="text-left text-xs font-medium px-4 py-3">Status</th>
              <th className="text-left text-xs font-medium px-4 py-3">Submitted</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sandBeige/60">
            {loading && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-warmGrey">Lade …</td></tr>
            )}
            {!loading && data?.items?.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-warmGrey">Keine Treffer.</td></tr>
            )}
            {data?.items?.map((s) => (
              <tr key={s.id} className="hover:bg-ivory/60 transition-colors">
                <td className="px-5 py-3.5">
                  <Link to={`/admin/startups/${s.id}`} className="font-medium text-cocoa hover:text-raspberry" data-testid={`row-startup-${s.id}`}>
                    {s.company?.name || "—"}
                  </Link>
                  <p className="text-xs text-warmGrey truncate max-w-md">{s.company?.one_liner}</p>
                </td>
                <td className="px-4 py-3.5 text-sm text-cocoa">{s.details?.stage || "—"}</td>
                <td className="px-4 py-3.5 text-sm text-cocoa">{s.company?.country || "—"}</td>
                <td className="px-4 py-3.5 text-sm text-cocoa">
                  {s.latest_ai?.overall_fit_score != null ? (
                    <span className="inline-flex items-center gap-1.5">
                      <strong>{s.latest_ai.overall_fit_score}</strong>
                      {s.latest_ai.is_mocked && <span className="text-[10px] uppercase text-warmGrey">mock</span>}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-4 py-3.5"><StatusBadge value={s.status} size="sm" /></td>
                <td className="px-4 py-3.5 text-xs text-warmGrey">{s.created_at ? new Date(s.created_at).toLocaleDateString("de-DE") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-sandBeige/60">
          {!loading && data?.items?.length === 0 && (
            <p className="px-5 py-10 text-center text-warmGrey">Keine Treffer.</p>
          )}
          {data?.items?.map((s) => (
            <Link
              key={s.id}
              to={`/admin/startups/${s.id}`}
              className="block px-5 py-4 hover:bg-ivory/60 transition-colors"
              data-testid={`card-startup-${s.id}`}
            >
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-cocoa truncate">{s.company?.name || "—"}</p>
                  <p className="text-xs text-warmGrey truncate">{s.company?.one_liner}</p>
                  <p className="text-xs text-warmGrey mt-1">
                    {s.details?.stage} · {s.company?.country}
                    {s.latest_ai?.overall_fit_score != null && (
                      <> · AI {s.latest_ai.overall_fit_score}</>
                    )}
                  </p>
                </div>
                <StatusBadge value={s.status} size="sm" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-warmGrey">
        <div className="flex items-center gap-2">
          <span>Pro Seite:</span>
          {PAGE_SIZES.map((n) => (
            <button
              key={n} type="button"
              onClick={() => updateParam("limit", n)}
              className={`wit-pill text-xs ${limit === n ? "bg-raspberry text-white border-raspberry" : "bg-white border-sandBeige text-cocoa"}`}
              data-testid={`pagesize-${n}`}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span>{total} Treffer</span>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => updateParam("page", page - 1)}
            className="wit-pill bg-white border-sandBeige text-cocoa disabled:opacity-40"
          >
            ‹ Prev
          </button>
          <span className="text-cocoa">{page} / {totalPages}</span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => updateParam("page", page + 1)}
            className="wit-pill bg-white border-sandBeige text-cocoa disabled:opacity-40"
          >
            Next ›
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- helpers ----
const ChipGroup = ({ label, options, isSelected, onToggle, testIdPrefix }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-xs font-medium text-cocoa">{label}</span>
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const sel = isSelected(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
              sel
                ? "bg-raspberry text-white border-raspberry"
                : "bg-white border-sandBeige text-cocoa hover:border-raspberry"
            }`}
            data-testid={`${testIdPrefix}-${opt}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  </div>
);
